<template>
  <section class="hotel-assignment">
    <h2 class="hotel-title">Заселение в отель</h2>

    <!-- Нет данных -->
    <div v-if="authStore.totalRooms === 0" class="hotel-state">
      <p>Ваша заявка для расселения в отель пока не готова.</p>
    </div>

    <!-- Успешно отправлено -->
    <div
      v-else-if="
        authStore.totalRooms > 0 &&
        !hotelStore.hasRooms &&
        !hotelStore.loading &&
        !hotelStore.error
      "
      class="hotel-success"
    >
      <h3>✓ Данные успешно отправлены в отель</h3>
    </div>

    <!-- Основной контент -->
    <div
      v-else-if="hotelStore.hasRooms && !hotelStore.loading"
      class="hotel-content"
    >
      <!-- Информация -->
      <section class="hotel-info">
        <p class="hotel-description">
          Заполните все предоставленные комнаты и информацию ниже, после чего
          нажмите кнопку <strong>«Отправить данные»</strong>. Если у
          проживающего на момент заселения происходит процесс получения/смены
          паспорта, просьба присылать данные, которые присутствуют у гостя на
          данный момент (свидетельство о рождении/справка о приеме документов на
          оформление паспорта). Обращаем внимание, что без оригинала документов
          отель не сможет Вас заселить!
          <br />
          <span class="hotel-warning">
            После отправки внести изменения будет невозможно
          </span>
        </p>

        <div class="hotel-stats">
          <div class="stat">
            <span class="stat-label">Номеров</span>
            <span class="stat-value">{{ hotelStore.totalRooms }}</span>
          </div>

          <div class="stat">
            <span class="stat-label">Мест</span>
            <span class="stat-value">{{ hotelStore.totalBeds }}</span>
          </div>

          <div v-if="hotelStore.occupiedBeds" class="stat stat-filled">
            <span class="stat-label">Заполнено</span>
            <span class="stat-value">
              {{ hotelStore.occupiedBeds }} / {{ hotelStore.totalBeds }}
            </span>
          </div>
        </div>
      </section>

      <!-- Блок руководителя -->
      <div class="leader-card">
        <h3 class="leader-title">Информация о руководителе и коллективе</h3>

        <div class="leader-grid">
          <div class="leader-field">
            <input
              v-model="hotelStore.leaderFio"
              placeholder="ФИО руководителя *"
            />
          </div>

          <div class="leader-field">
            <input
              v-model="hotelStore.leaderPassport"
              placeholder="Серия/номер паспорта *"
            />
          </div>

          <div class="leader-field leader-full">
            <input
              v-model="hotelStore.groupName"
              placeholder="Название коллектива *"
            />
          </div>

          <div class="leader-field">
            <label>Прибытие *</label>
            <div class="leader-inline">
              <input type="date" v-model="hotelStore.arrivalDate" />
              <input type="time" v-model="hotelStore.arrivalTime" />
            </div>
          </div>

          <div class="leader-field">
            <label>Место прибытия *</label>
            <input
              v-model="hotelStore.arrivalPlace"
              placeholder="Аэропорт, вокзал"
            />
          </div>

          <div class="leader-field">
            <label>Отъезд *</label>
            <div class="leader-inline">
              <input type="date" v-model="hotelStore.departureDate" />
              <input type="time" v-model="hotelStore.departureTime" />
            </div>
          </div>

          <div class="leader-field">
            <label>Место отъезда *</label>
            <input
              v-model="hotelStore.departurePlace"
              placeholder="Аэропорт, вокзал"
            />
          </div>
        </div>
      </div>

      <!-- Комнаты -->
      <template v-for="(rooms, capacity) in groupedRooms" :key="capacity">
        <section v-if="rooms.length" class="rooms-block">
          <h3 class="block-title">{{ capacity }}-местные</h3>

          <div class="rooms-grid">
            <div
              v-for="roomKey in rooms"
              :key="roomKey"
              class="room-card"
              :class="[
                `room-${capacity}`,
                { filled: hasOccupiedBeds(roomKey) },
              ]"
              @click="hotelStore.openModal(roomKey)"
            >
              <div class="beds-summary">
                <div
                  v-for="(person, bedKey) in hotelStore.rooms[roomKey]"
                  :key="bedKey"
                  class="bed-slot-summary"
                >
                  <p class="person-fio">
                    {{ formatFio(person.FIO) }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </template>
    </div>
    <div
      style="
        background: #fff3cd;
        padding: 12px;
        margin: 16px 0;
        border: 1px solid #ffeeba;
        border-radius: 6px;
        font-family: monospace;
      "
       v-if="!hotelStore.isLeaderFormValid && authStore.totalRooms > 0"
    >
      <div
        v-if="authStore.totalRooms > 0"
        class="leader-form-status"
        :class="{
          'text-red-600 bg-red-50 p-4 rounded-lg':
            !hotelStore.isLeaderFormValid,
        }"
        style="margin: 16px 0; font-weight: 500"
      >
        <template v-if="!hotelStore.isLeaderFormValid">
          Форма руководителя не заполнена
          <span style="font-size: 0.9em; opacity: 0.9"
            >(кнопка отправки будет активна только после заполнения всех
            полей)</span
          >
        </template>

        <template v-else> Форма руководителя заполнена ✓ </template>
      </div>
      <div
        v-if="hotelStore.occupiedBeds < hotelStore.totalBeds"
        style="margin-top: 8px"
      >
        Не все кровати заполнены (ещё нужно:
        {{ hotelStore.totalBeds - hotelStore.occupiedBeds }})
      </div>
    </div>
    <!-- Кнопка отправки -->
    <div class="send-section">
      <button
        class="send-button"
        @click="sendHotelRooms"
        v-if="authStore.totalRooms > 0"
        :disabled="
          hotelStore.loading ||
          hotelStore.occupiedBeds < hotelStore.totalBeds ||
          !hotelStore.isLeaderFormValid
        "
      >
        <span v-if="hotelStore.loading">Отправка данных…</span>
        <span v-else>Отправить данные</span>
      </button>
    </div>
    <!-- Загрузка -->
    <div v-if="hotelStore.loading" class="spinner-container">
      <div class="spinner"></div>
      <p>Загружаем структуру номеров…</p>
    </div>

    <!-- Ошибка -->
    <div v-if="hotelStore.error" class="status error">
      {{ hotelStore.error }}
      <button class="retry" @click="loadHotelRooms">Повторить загрузку</button>
    </div>

    <!-- Модальное окно редактирования комнаты -->
    <div
      v-if="hotelStore.modalOpen"
      class="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="hotelStore.closeModal"
    >
      <div
        class="modal-content bg-white rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <h3 class="modal-heading text-xl font-bold mb-4">
          Заполните данные жильцов комнаты {{ hotelStore.selectedRoomKey }}
        </h3>

        <div class="beds-edit space-y-6">
          <div
            v-for="(person, bedKey) in hotelStore.localRoomData"
            :key="bedKey"
            class="bed-edit-slot border-b pb-4 last:border-b-0"
          >
            <p class="bed-label font-medium mb-2">Кровать {{ bedKey }}</p>

            <input
              placeholder="ФИО"
              class="bed-input block w-full border rounded p-2 mb-2"
              v-model="person.FIO"
            />
            <input
              placeholder="Паспорт — серия и номер"
              class="bed-input block w-full border rounded p-2 mb-2"
              v-model="person.PASS"
            />
            <input
              placeholder="Номер телефона"
              class="bed-input block w-full border rounded p-2 mb-2"
              v-model="person.PHONE"
            />

            <!-- Новое поле -->
            <div class="birthdate-field mt-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Дата рождения
              </label>
              <input
                type="date"
                class="bed-input block w-full border rounded p-2"
                v-model="person.BIRTHDATE"
              />
            </div>
          </div>
        </div>

        <div class="modal-actions flex justify-end gap-3 mt-6">
          <button
            class="button-1 save-button"
            @click="hotelStore.saveRoom(authStore.token, recaptcha)"
            :disabled="hotelStore.updateLoading || hotelStore.updateSuccess"
          >
            <span v-if="hotelStore.updateLoading">Сохранение…</span>
            <span v-else-if="hotelStore.updateSuccess">✓ Сохранено</span>
            <span v-else>Сохранить</span>
          </button>

          <button
            class="button-1"
            @click="hotelStore.closeModal"
            :disabled="hotelStore.updateLoading"
          >
            Отмена
          </button>
        </div>

        <div v-if="hotelStore.updateError" class="error">
          {{ hotelStore.updateError }}
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useAuthStore } from "@/stores/auth";
import { useHotelStore } from "@/stores/hotel";
import { useReCaptcha } from "vue-recaptcha-v3"; // ← добавь импорт

export default {
  name: "HotelAssignment",

  setup() {
    const authStore = useAuthStore();
    const hotelStore = useHotelStore();
    const recaptcha = useReCaptcha(); // ← здесь работает!

    return { authStore, hotelStore, recaptcha };
  },

  computed: {
    roomCounts() {
      return {
        singles: this.authStore.singleRoomsCount,
        doubles: this.authStore.doubleRoomsCount,
        triples: this.authStore.tripleRoomsCount,
        quads: this.authStore.quadRoomsCount,
      };
    },

    groupedRooms() {
      const groups = { 1: [], 2: [], 3: [], 4: [] };
      for (const key of this.hotelStore.sortedRoomKeys) {
        const cap = Number(key.split(".")[0]);
        if (groups[cap]) groups[cap].push(key);
      }
      return groups;
    },
  },

  watch: {
    "authStore.totalRooms": {
      handler(newValue) {
        if (
          newValue > 0 &&
          this.authStore.isFullyLoaded &&
          !this.hotelStore.hasRooms &&
          !this.hotelStore.loading &&
          !this.hotelStore.error
        ) {
          this.loadHotelRooms();
        }
      },
      immediate: true,
    },
  },

  methods: {
    hasOccupiedBeds(roomKey) {
      const beds = this.hotelStore.rooms[roomKey] || {};
      return Object.values(beds).some((person) => person?.FIO?.trim());
    },

    async loadHotelRooms() {
      try {
        await this.hotelStore.loadHotelRooms(
          this.authStore.token,
          this.roomCounts,
          this.recaptcha // ← передаём объект recaptcha
        );
      } catch (err) {
        console.error("Ошибка при загрузке комнат:", err);
        this.hotelStore.setError(
          "Не удалось загрузить номера. Попробуйте позже."
        );
      }
    },

    async sendHotelRooms() {
      try {
        await this.hotelStore.sendHotelRooms(
          this.authStore.token,
          this.recaptcha // ← тоже передаём
        );
      } catch (err) {
        console.error("Ошибка отправки данных:", err);
        this.hotelStore.setError(
          "Не удалось отправить данные. Попробуйте позже."
        );
      }
    },

    formatFio(fio) {
      if (!fio?.trim()) return "—";
      const parts = fio.trim().split(" ");
      if (parts.length === 1) return parts[0];
      if (parts.length === 2) return `${parts[0]} ${parts[1][0]}.`;
      return `${parts[0]} ${parts[1][0]}.${parts[2][0]}.`;
    },
    formatBirthdate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      if (isNaN(date)) return "—";
      // Варианты:
      // return date.toLocaleDateString('ru-RU');           // 15.03.2001
      return date.getFullYear(); // 2001
      // return dateStr.split('-').reverse().join('.');     // 15.03.2001
    },
  },
};
</script>
<style scoped lang="scss">
.birthdate-field {
  label {
    font-size: 0.85rem;
    color: #555;
  }
  input[type="date"] {
    font-size: 0.95rem;
    padding: 0.6rem;
    background: #fff;
  }
}

.person-birthdate {
  font-size: 0.78rem;
  color: #444;
  opacity: 0.9;
  text-align: center;
}
/* ==========================
   ОБЩИЕ СТИЛИ
========================== */
.hotel-assignment {
  max-width: 100%;
  margin: auto;
  padding-bottom: 3rem;
}
/* =================================================
   ЗАГОЛОВОК
================================================= */
.hotel-header {
  margin-bottom: 1.5rem;
}

.hotel-title {
  font-size: 1.8rem;
  font-weight: 800;
}

/* =================================================
   СОСТОЯНИЯ
================================================= */
.hotel-state {
  padding: 1.5rem;
  border-radius: 14px;
  background: #f5f5f5;
  color: #666;
  font-style: italic;
}

.hotel-success {
  padding: 1.6rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(102, 255, 191, 0.35),
    rgba(133, 133, 133, 0.2)
  );
  border: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 600;
}
/* =================================================
   ОСНОВНОЙ КОНТЕНТ
================================================= */
.hotel-content {
  display: flex;
  flex-direction: column;
  gap: 2.2rem;
}

/* =================================================
   ИНФОРМАЦИЯ И СТАТИСТИКА
================================================= */
.hotel-info {
  padding: 1.8rem;
  border-radius: 18px;
  background: rgba(25, 210, 161, 0.15);
  backdrop-filter: blur(12px);
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.15);
}

.hotel-description {
  margin-bottom: 1.4rem;
  line-height: 1.5;
}

.hotel-warning {
  color: #b71c1c;
  font-weight: 600;
}

.hotel-stats {
  display: flex;
  gap: 1.4rem;
  flex-wrap: wrap;
}

.stat {
  padding: 0.8rem 1.2rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.75);
  min-width: 120px;
}

.stat-filled {
  background: rgba(25, 118, 210, 0.15);
}

.stat-label {
  display: block;
  font-size: 0.75rem;
  color: #555;
}

.stat-value {
  font-size: 1.2rem;
  font-weight: 700;
}

/* =================================================
   КАРТОЧКА РУКОВОДИТЕЛЯ
================================================= */
.leader-card {
  padding: 2rem;
  border-radius: 20px;
  background: rgba(25, 210, 161, 0.15);
  backdrop-filter: blur(14px) saturate(150%);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
}

.leader-title {
  margin-bottom: 1.6rem;
  font-size: 1.35rem;
  font-weight: 700;
}

.leader-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.4rem 1.6rem;
}

.leader-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.leader-full {
  grid-column: span 2;
}

.leader-field label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #444;
}

.leader-field input {
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  transition: 0.2s;
}

.leader-field input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.25);
}

.leader-inline {
  display: flex;
  gap: 0.6rem;
}

.leader-warning {
  margin-top: 1.2rem;
  padding: 0.8rem 1rem;
  background: rgba(255, 0, 0, 0.08);
  border-left: 4px solid #c62828;
  border-radius: 8px;
  color: #b71c1c;
  font-size: 0.85rem;
}

/* =================================================
   КНОПКА ОТПРАВКИ
================================================= */
.send-section {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}

.send-button {
  padding: 0.9rem 2.6rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #1e88e5, #1565c0);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: 0.25s;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.45);
}

.send-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(21, 101, 192, 0.55);
}

.send-button:disabled {
  background: #9e9e9e;
  cursor: not-allowed;
  box-shadow: none;
}

/* =================================================
   ОШИБКА
================================================= */
.retry {
  background: linear-gradient(135deg, #ef5350, #c62828);
}

.status {
  padding: 1rem 1.2rem;
  border-radius: 12px;
  margin: 1rem 0;
}

.error {
  background: #ffebee;
  color: #c62828;
}

.empty {
  color: #757575;
  font-style: italic;
}

/* ==========================
   СПИННЕР
========================== */
.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin: 1rem 0;
  background: #e3f2fd;
  border-radius: 12px;
  color: #1565c0;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #1565c0;
  animation: spin 1s ease infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* ==========================
   БЛОКИ КОМНАТ
========================== */
.rooms-block {
  margin-top: 3rem;
}

.block-title {
  width: 100%;
  margin-bottom: 1.6rem;
  font-size: 1.35rem;
  font-weight: 700;
}

.rooms-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.room-card.filled {
  background: rgba(25, 118, 210, 0.15);
}

/* ==========================
   КАРТОЧКИ КОМНАТ
========================== */
.room-card {
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  padding: 0.4rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  background: rgba(25, 210, 161, 0.15);
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(4px) saturate(150%);
  box-sizing: border-box;
  overflow: hidden;
  transition: all 0.18s ease;
}

.room-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

/* Размеры и количество в ряд */
.room-1 {
  width: 12vw; /* ~4 в ряд на десктопе */
  aspect-ratio: 1 / 1.2;
  min-width: 140px;
  min-height: 140px;
}

.room-2 {
  width: 12vw;
  aspect-ratio: 1 / 1.2;
  min-width: 140px;
  min-height: 180px;
}

.room-3 {
  width: calc(33% - 0.5rem); /* 2 в ряд */
  aspect-ratio: 1.6 / 1;
  min-height: 280px;
}
.room-4 {
  width: calc(40% - 0.5rem); /* 2 в ряд */
  aspect-ratio: 1.6 / 1;
  min-height: 280px;
}

/* Номер комнаты */
.room-card h4 {
  font-size: 0.82rem;
  font-weight: 600;
  position: absolute;
  top: 8px;
  left: 10px;
  margin: 0;
  color: #555;
  z-index: 2;
  background: rgba(255, 255, 255, 0.75);
  padding: 2px 6px;
  border-radius: 6px;
}

/* ==========================
   КРОВАТИ
========================== */
.beds-summary {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
}

.bed-slot-summary {
  background: url(./bed7.svg) center / contain no-repeat;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.2s, transform 0.15s;
  position: absolute;

  width: 10vw;
  height: 10.5vw;
}

.bed-slot-summary.empty {
  opacity: 0.32;
}

.person-fio {
  font-size: 1.2vw;
  font-weight: 800;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150%;
  padding: 0 8px;
  color: #222;
  text-shadow: 0 0 3px rgba(255, 255, 255, 0.9);
  transform: translate(18px, -10px);
}

/* Расстановка кроватей */
.room-1 .bed-slot-summary:nth-child(1) {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Двухместные — вертикально по центру */
.room-2 .bed-slot-summary:nth-child(1) {
  top: 28%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.room-2 .bed-slot-summary:nth-child(2) {
  top: 72%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Трёхместные — ближе к углам */
.room-3 .bed-slot-summary:nth-child(1) {
  top: 2%;
  left: 2%;
}

.room-3 .bed-slot-summary:nth-child(2) {
  top: 2%;
  right: 2%;
}

.room-3 .bed-slot-summary:nth-child(3) {
  bottom: 2%;
  left: 50%;
  transform: translateX(-50%);
}

/* Четырёхместные — почти в углах */
.room-4 .bed-slot-summary:nth-child(1) {
  top: 2%;
  left: 2%;
}

.room-4 .bed-slot-summary:nth-child(2) {
  top: 2%;
  right: 2%;
}

.room-4 .bed-slot-summary:nth-child(3) {
  bottom: 2%;
  left: 2%;
}

.room-4 .bed-slot-summary:nth-child(4) {
  bottom: 2%;
  right: 2%;
}

/* ==========================
   МОДАЛЬНОЕ ОКНО
========================== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.774);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 6;
}

.modal-content {
  position: relative;
  max-width: 400px;
  width: 90%;
  max-height: 60vh;
  overflow-y: auto;
  padding: 2vw 4vw 4vw 3vw;
  border-radius: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  color: #fff;
  animation: fadeIn 0.3s ease;

  /* Жидкое стекло */
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.345),
    rgba(133, 133, 133, 0.226)
  );
  backdrop-filter: blur(12px) saturate(150%);
  -webkit-backdrop-filter: blur(4px) saturate(150%);
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.modal-heading {
  margin: 0;
}

.beds-edit {
  display: grid;
  gap: 1.2rem;
}

.bed-edit-slot {
  border-radius: 12px;
  padding: 1rem;
  background: rgb(229, 229, 229);
  display: flex;
  flex-direction: column;
  gap: 1vw;
  align-items: center;
}

.bed-label {
  display: block;
  margin: 0;
  color: #262626;
  font-weight: 500;
}
.modal-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2vw;
  justify-content: center;
}
.bed-input {
  width: 70%;
}
.error {
  margin-top: 1.2rem;
  padding: 0.8rem;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  text-align: center;
}

@media (max-width: 768px) {
  .leader-grid {
    grid-template-columns: 1fr;
  }

  .leader-full {
    grid-column: span 1;
  }
  .person-fio {
    font-size: 3.5vw;
  }
  .room-1,
  .room-2 {
    width: calc(50% - 0.5rem);
  }

  .room-3,
  .room-4 {
    width: 100%;
  }

  .bed-slot-summary {
    width: 28vw;
    height: 22vw;
    min-width: 80px;
    min-height: 40px;
  }
  .person-fio {
    font-size: 3.5vw;
  }
  .modal-heading {
    font-size: 4vw;
  }
  .modal-content {
    padding: 10vw 6vw 10vw 6vw;
  }

  .hotel-stats {
    flex-wrap: wrap;

    .stat {
      flex: 1 1 auto; // растягиваем первые два
    }

    .stat-filled {
      width: 100%; // третий блок на весь ряд
      order: 3; // ставим под первые два
    }
  }
}
</style>


